// CityAutocomplete.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { City, State, Country } from "country-state-city";

export default function CityAutocomplete({ name = "city", onSelect }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // hidden field value: "City, StateCode, CountryCode"
  const [value, setValue] = useState("");
  const boxRef = useRef(null);

  // Build country/state maps and city list once, for all countries
  const { options, index } = useMemo(() => {
    // All countries
    const countries = Country.getAllCountries();
    const countryMap = Object.fromEntries(
      countries.map((c) => [c.isoCode, c.name])
    );

    // All states
    const states = State.getAllStates();
    // key: "countryCode-stateCode"
    const stateMap = Object.fromEntries(
      states.map((s) => [`${s.countryCode}-${s.isoCode}`, s.name])
    );

    // All cities
    const cities = City.getAllCities().map((c) => {
      const countryName = countryMap[c.countryCode] || c.countryCode;
      const stateName =
        stateMap[`${c.countryCode}-${c.stateCode}`] || c.stateCode || "";
      const label = stateName
        ? `${c.name}, ${stateName}, ${countryName}`
        : `${c.name}, ${countryName}`;
      const value = `${c.name}, ${c.stateCode}, ${c.countryCode}`;
      return {
        label,
        value,
        name: c.name.toLowerCase(),
        state: (stateName || "").toLowerCase(),
        country: (countryName || "").toLowerCase(),
      };
    });

    const idx = cities.map((o) => ({
      s: o.label.toLowerCase(), // full string
      i: o.label
        .toLowerCase()
        .split(/[ ,]+/)
        .map((w) => w[0])
        .join(""), // initials from all words
    }));

    return { options: cities, index: idx };
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current || boxRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Filter: prefix on city/state/country, or initials on full label
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    for (let i = 0; i < options.length && out.length < 12; i++) {
      const o = options[i];
      const idx = index[i];
      if (
        o.name.startsWith(q) ||
        o.state.startsWith(q) ||
        o.country.startsWith(q) ||
        idx.s.startsWith(q) ||
        idx.i.startsWith(q.replace(/[^a-z]/g, ""))
      ) {
        out.push(o);
      }
    }
    return out;
  }, [query, options, index]);

  const choose = (opt) => {
    setQuery(opt.label);
    setValue(opt.value);
    setOpen(false);
    setHighlight(0);
    onSelect?.(opt);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) choose(opt);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="position-relative">
      <input
        type="text"
        className="form-control"
        placeholder="Start typing city, state or country…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      <input type="hidden" name={name} value={value} />

      {open && filtered.length > 0 && (
        <ul
          className="list-group"
          style={{
            position: "absolute",
            zIndex: 1050,
            width: "100%",
            maxHeight: 240,
            overflowY: "auto",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          {filtered.map((opt, i) => (
            <li
              key={`${opt.value}-${i}`}
              className={`list-group-item list-group-item-action ${
                i === highlight ? "active" : ""
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(opt)}
              onMouseEnter={() => setHighlight(i)}
              style={{ cursor: "pointer" }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
